import { SupabaseErrorLike, isSupabaseError } from '@/types/auth';

export interface RateLimitError {
  isRateLimit: boolean;
  message: string;
  suggestedWaitTime?: number; // em minutos
  errorType: 'email' | 'signup' | 'general' | 'unknown';
}

export const parseSupabaseError = (error: unknown): RateLimitError => {
  // Handle null/undefined
  if (!error) {
    return {
      isRateLimit: false,
      message: 'Erro desconhecido',
      errorType: 'unknown'
    };
  }

  // Extract error properties safely
  const errorMessage = isSupabaseError(error) ? error.message || '' : 
                       error instanceof Error ? error.message : '';
  const errorLower = errorMessage.toLowerCase();
  const errorCode = isSupabaseError(error) ? error.error_code || '' : '';
  const status = isSupabaseError(error) ? error.status : undefined;

  // Detectar rate limit específico do Supabase (do log)
  if (errorCode === 'over_email_send_rate_limit' || errorLower.includes('email rate limit exceeded')) {
    return {
      isRateLimit: true,
      message: 'Limite de emails do Supabase atingido. Este é um limite do servidor, aguarde alguns minutos.',
      suggestedWaitTime: 10, // 10 minutos mais realista
      errorType: 'email'
    };
  }

  // Detectar timeout do servidor
  if (errorLower.includes('context deadline exceeded') || errorCode === 'request_timeout' || status === 504) {
    return {
      isRateLimit: true,
      message: 'Timeout do servidor Supabase. Tente novamente em alguns minutos.',
      suggestedWaitTime: 2,
      errorType: 'general'
    };
  }

  // Detectar rate limit de email geral
  if (errorLower.includes('email rate limit') || errorLower.includes('email send rate limit')) {
    return {
      isRateLimit: true,
      message: 'Limite de envio de emails atingido. Aguarde alguns minutos antes de tentar novamente.',
      suggestedWaitTime: 5,
      errorType: 'email'
    };
  }

  // Detectar rate limit geral (429)
  if (errorLower.includes('rate limit') || status === 429) {
    return {
      isRateLimit: true,
      message: 'Limite do servidor atingido. Aguarde alguns minutos antes de tentar novamente.',
      suggestedWaitTime: 8,
      errorType: 'signup'
    };
  }

  // Detectar outros erros relacionados a tentativas excessivas
  if (errorLower.includes('too many') || errorLower.includes('exceeded')) {
    return {
      isRateLimit: true,
      message: 'Limite de tentativas excedido. Aguarde antes de tentar novamente.',
      suggestedWaitTime: 3,
      errorType: 'general'
    };
  }

  // Detectar timeout
  if (errorLower.includes('timeout')) {
    return {
      isRateLimit: false,
      message: 'Tempo limite excedido. Verifique sua conexão e tente novamente.',
      errorType: 'unknown'
    };
  }

  // Outros erros de email
  if (errorLower.includes('email')) {
    return {
      isRateLimit: false,
      message: 'Erro relacionado ao email. Verifique se o email está correto e tente novamente.',
      errorType: 'email'
    };
  }

  // Credenciais inválidas
  if (errorLower.includes('invalid login credentials') || errorLower.includes('invalid credentials')) {
    return {
      isRateLimit: false,
      message: 'Credenciais inválidas. Verifique seu email e senha.',
      errorType: 'unknown'
    };
  }

  return {
    isRateLimit: false,
    message: errorMessage || 'Erro inesperado. Tente novamente.',
    errorType: 'unknown'
  };
};

export const getRetryMessage = (errorType: string, waitTime?: number): string => {
  if (waitTime) {
    return `Aguarde ${waitTime} minuto${waitTime === 1 ? '' : 's'} antes de tentar novamente.`;
  }

  switch (errorType) {
    case 'email':
      return 'Aguarde alguns minutos antes de tentar novamente devido ao limite de emails.';
    case 'signup':
      return 'Aguarde alguns minutos antes de fazer um novo cadastro.';
    case 'timeout':
      return 'Conexão lenta detectada. O sistema está tentando automaticamente...';
    default:
      return 'Aguarde alguns minutos antes de tentar novamente.';
  }
};
